import { SxProps, Theme, alpha } from "@mui/material";
import { inPersonTokens as t } from "./InPersonSigning.tokens";

/**
 * Hand-off shell styling. Built as a `(theme) => ({...})` factory so every
 * colour is pulled from `theme.palette.signer.*` — the same purple/orange/ink
 * system the signing surface uses — with no hardcoded hex.
 */
const inPersonStyles = (theme: Theme) => {
  const signer = theme.palette.signer;
  const white = theme.palette.common.white;
  // Solid CTA fills track the app brand colour, not the design's orange —
  // see theme/colors.ts §11.5 "orange fills → brand red".
  const primary = theme.palette.primary;

  const consentControl = {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    m: 0,
    // `FormControlLabel` renders `inline-flex` by default, sized to its
    // content rather than the 100% width above unless forced — that's what
    // was pushing the label past the dialog's edge into a horizontal
    // scrollbar instead of wrapping. `minWidth: 0` on the label span is what
    // actually lets a flex child wrap instead of forcing its parent wider.
    display: "flex",
    alignItems: "flex-start",
    gap: t.consent.gap,
    p: t.consent.padding,
    borderRadius: t.consent.radius,
    textAlign: "left",
    bgcolor: signer.surface.subtle,
    border: `1px solid ${signer.border.light}`,
    "& .MuiFormControlLabel-label": {
      minWidth: 0,
      flex: 1,
      overflowWrap: "anywhere",
    },
  };

  const styles: Record<string, SxProps<Theme>> = {
    // ---- Full-screen overlay (interstitial / completion / summary) ----------
    overlay: {
      position: "fixed",
      inset: 0,
      zIndex: t.overlayZ,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: { xs: "16px", sm: 3 },
      overflowY: "auto",
      background: `
        radial-gradient(90% 70% at 80% -10%, ${alpha(
          signer.purple.main,
          0.55,
        )}, transparent 60%),
        radial-gradient(70% 55% at 5% 105%, ${alpha(
          signer.gradient.coral,
          0.22,
        )}, transparent 55%),
        ${signer.ink}`,
    },

    card: {
      width: "100%",
      maxWidth: `${t.card.maxWidth}px`,
      // No scroll region of its own — a boxed-in scrollbar inside a card
      // that's already sitting inside a full-page overlay looks broken. If a
      // short viewport can't fit it, the overlay scrolls the whole page.
      flexShrink: 0,
      bgcolor: white,
      borderRadius: t.card.radius,
      p: { xs: t.card.padding, sm: t.card.paddingWide },
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: t.card.gap,
      boxShadow: `0 24px 60px ${alpha(signer.ink, 0.4)}`,
      boxSizing: "border-box",
    },

    iconCircleOrange: {
      width: t.iconCircle,
      height: t.iconCircle,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: signer.orange.main,
      bgcolor: signer.orange.tint,
      flexShrink: 0,
      "& svg": { fontSize: t.iconGlyph },
    },
    iconCircleSuccess: {
      width: t.iconCircle,
      height: t.iconCircle,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: signer.success.main,
      bgcolor: signer.success.tint,
      flexShrink: 0,
      "& svg": { fontSize: t.iconGlyph },
    },
    // The public host-link page's "link doesn't work" / "nothing to do" states
    // have no logged-in dashboard to fall back to, so they get their own
    // (muted, non-orange/-success) icon treatment rather than reusing either.
    iconCircleMuted: {
      width: t.iconCircle,
      height: t.iconCircle,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: signer.text.meta,
      bgcolor: signer.surface.subtle,
      flexShrink: 0,
      "& svg": { fontSize: t.iconGlyph },
    },
    // Voided / expired / declined — the host-link page's one error-toned state.
    iconCircleDanger: {
      width: t.iconCircle,
      height: t.iconCircle,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: signer.danger.main,
      bgcolor: signer.danger.tint,
      flexShrink: 0,
      "& svg": { fontSize: t.iconGlyph },
    },

    title: {
      fontSize: t.title.size,
      fontWeight: t.title.weight,
      lineHeight: t.title.lineHeight,
      letterSpacing: t.title.letterSpacing,
      color: signer.ink,
    },

    subtitle: {
      fontSize: t.subtitle.size,
      lineHeight: t.subtitle.lineHeight,
      color: signer.text.meta,
      maxWidth: 320,
    },

    signerChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      borderRadius: t.signerChip.radius,
      p: t.signerChip.padding,
      bgcolor: signer.purple.tint,
      border: `1px solid ${signer.purple.border}`,
    },
    signerChipLabel: {
      fontSize: t.signerChip.labelSize,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: signer.purple.main,
    },
    signerChipName: {
      fontSize: t.signerChip.nameSize,
      fontWeight: 800,
      color: signer.ink,
    },

    // ---- Capture modal ("Who's signing in person?") chrome + fields --------
    modalTitle: {
      fontSize: "17px",
      fontWeight: 800,
      color: signer.ink,
    },
    modalSubtitle: {
      fontSize: "12.5px",
      lineHeight: 1.6,
      color: signer.text.meta,
    },
    modalFieldLabel: {
      fontSize: "12px",
      fontWeight: 700,
      color: signer.ink,
    },
    modalFieldInput: {
      "& .MuiOutlinedInput-root": {
        height: "42px",
        borderRadius: "9px",
        bgcolor: signer.surface.subtle,
        "& fieldset": { borderColor: signer.border.main },
        "&:hover fieldset": { borderColor: signer.border.strong },
        "&.Mui-focused fieldset": { borderColor: signer.orange.main },
        "&.Mui-error fieldset": { borderColor: signer.danger.main },
      },
      "& .MuiOutlinedInput-input": {
        padding: "0 13px",
        fontSize: "13px",
        fontWeight: 500,
        color: signer.ink,
        "&::placeholder": { color: signer.text.meta, opacity: 1 },
      },
    },
    modalNote: {
      mt: "12px",
      fontSize: "11.5px",
      lineHeight: 1.5,
      color: signer.text.meta,
    },

    // ---- Consent acknowledgement (host dialog + signer hand-off card) -------
    consentControl,
    // Composed here rather than spread at the call site — spreading two
    // `SxProps` inside a single `sx` prop does not type-check.
    consentControlInvalid: {
      ...consentControl,
      bgcolor: signer.danger.tint,
      border: `1px solid ${signer.danger.border}`,
    },
    consentCheckbox: {
      p: 0,
      mt: "1px",
      color: signer.border.strong,
      "&.Mui-checked": { color: signer.orange.dark },
      "& .MuiSvgIcon-root": { fontSize: t.consent.checkboxSize },
    },
    consentLabel: {
      fontSize: t.consent.labelSize,
      lineHeight: t.consent.lineHeight,
      color: signer.ink,
    },
    fieldHint: {
      mt: "6px",
      fontSize: t.consent.errorSize,
      lineHeight: 1.45,
      color: signer.text.meta,
    },
    consentHint: {
      width: "100%",
      textAlign: "center",
      fontSize: t.consent.errorSize,
      color: signer.text.meta,
    },
    consentError: {
      width: "100%",
      mt: "6px",
      textAlign: "left",
      fontSize: t.consent.errorSize,
      color: signer.danger.main,
    },

    // ---- "Would you like a copy?" offer (completion screen) ----------------
    copyBlock: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: t.copy.gap,
      textAlign: "left",
      p: t.copy.padding,
      borderRadius: t.copy.radius,
      bgcolor: signer.surface.subtle,
      border: `1px solid ${signer.border.main}`,
    },
    copyPrompt: {
      fontSize: t.copy.promptSize,
      fontWeight: 600,
      color: signer.ink,
    },

    // ---- Hand-off picker (several actionable signers at once) --------------
    pickerBlock: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: t.picker.gap,
      textAlign: "left",
      p: t.picker.padding,
      borderRadius: t.picker.radius,
      bgcolor: signer.surface.subtle,
      border: `1px solid ${signer.border.main}`,
    },
    pickerLabel: {
      fontSize: t.picker.labelSize,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: signer.text.meta,
    },
    // Base and selected are complete objects rather than a base plus an
    // override, so the call site picks one instead of spreading two `SxProps`
    // together (TS2769 — see the styling rules).
    pickerOption: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "2px",
      width: "100%",
      boxSizing: "border-box",
      textAlign: "left",
      textTransform: "none",
      p: t.picker.optionPadding,
      borderRadius: t.picker.optionRadius,
      bgcolor: white,
      border: `1px solid ${signer.border.main}`,
      "&:hover": {
        bgcolor: signer.surface.muted,
        borderColor: signer.border.strong,
      },
    },
    pickerOptionActive: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "2px",
      width: "100%",
      boxSizing: "border-box",
      textAlign: "left",
      textTransform: "none",
      p: t.picker.optionPadding,
      borderRadius: t.picker.optionRadius,
      bgcolor: signer.orange.tint,
      border: `1px solid ${signer.orange.main}`,
      "&:hover": { bgcolor: signer.orange.hover },
    },
    pickerOptionName: {
      fontSize: t.picker.nameSize,
      fontWeight: 700,
      color: signer.ink,
    },
    pickerOptionMeta: {
      fontSize: t.picker.metaSize,
      color: signer.text.meta,
    },

    // ---- "Waiting on" recipients (nothing to hand off yet) -----------------
    waitingBlock: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: t.waiting.gap,
      alignItems: "center",
    },
    waitingLabel: {
      fontSize: t.waiting.labelSize,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: signer.text.meta,
    },
    waitingChips: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: t.waiting.gap,
    },
    waitingChip: {
      fontSize: t.waiting.chipSize,
      fontWeight: 600,
      color: signer.text.secondary,
      borderRadius: t.waiting.chipRadius,
      p: t.waiting.chipPadding,
      bgcolor: signer.surface.muted,
      border: `1px solid ${signer.border.light}`,
    },

    actions: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: "10px",
      mt: "6px",
    },

    primaryButton: {
      height: { xs: t.primaryButton.heightMobile, sm: t.primaryButton.height },
      borderRadius: t.primaryButton.radius,
      textTransform: "none",
      fontSize: t.primaryButton.fontSize,
      fontWeight: t.primaryButton.fontWeight,
      color: white,
      bgcolor: primary.main,
      boxShadow: "none",
      "&:hover": { bgcolor: primary.dark, boxShadow: "none" },
      "&.Mui-disabled": { bgcolor: signer.disabled, color: white },
    },

    ghostButton: {
      textTransform: "none",
      fontSize: t.ghostButton.fontSize,
      fontWeight: t.ghostButton.fontWeight,
      color: signer.text.meta,
      "&:hover": { bgcolor: "transparent", color: signer.ink },
      "&.Mui-disabled": { color: signer.disabled },
    },

    // ---- Capture modal footer (Cancel + Start session, side by side) -------
    modalActions: {
      width: "100%",
      display: "flex",
      gap: t.modalButton.gap,
      mt: "20px",
    },
    modalCancelButton: {
      flex: 1,
      height: { xs: t.primaryButton.heightMobile, sm: t.primaryButton.height },
      borderRadius: t.primaryButton.radius,
      textTransform: "none",
      fontSize: t.modalButton.fontSize,
      fontWeight: t.modalButton.fontWeight,
      color: signer.text.meta,
      bgcolor: white,
      border: `1px solid ${signer.border.main}`,
      boxShadow: "none",
      "&:hover": { bgcolor: signer.surface.subtle, boxShadow: "none" },
      "&.Mui-disabled": { color: signer.disabled },
    },
    modalStartButton: {
      flex: 1,
      height: { xs: t.primaryButton.heightMobile, sm: t.primaryButton.height },
      borderRadius: t.primaryButton.radius,
      textTransform: "none",
      fontSize: t.modalButton.fontSize,
      fontWeight: t.modalButton.fontWeight,
      color: white,
      bgcolor: primary.main,
      boxShadow: "none",
      "&:hover": { bgcolor: primary.dark, boxShadow: "none" },
      "&.Mui-disabled": { bgcolor: signer.disabled, color: white },
    },

    // ---- Floating "Return to host" cluster (visible while signing) ----------
    // Docked bottom-left, content-sized — desktop only (`md` and up). Below
    // `md` the mobile signing screen has in-flow header/footer chrome
    // contesting every edge (sticky header up top, a Finish-button bar and an
    // always-on AI FAB down bottom — see the token comment), so a full pill
    // anywhere on that screen ends up sitting on top of something. `barCompact`
    // below is what phone/tablet gets instead.
    bar: {
      position: "fixed",
      left: t.bar.offset,
      bottom: t.bar.offset,
      zIndex: t.barZ,
      display: { xs: "none", md: "flex" },
      alignItems: "center",
      gap: t.bar.gap,
      p: t.bar.padding,
      borderRadius: t.bar.radius,
      bgcolor: white,
      border: `1px solid ${signer.border.main}`,
      boxShadow: `0 12px 34px ${alpha(signer.ink, 0.18)}`,
      // The bar is content-sized, so on a narrow phone the signer name plus the
      // button can run past the viewport. Pin the right edge below `sm` and let
      // the name column ellipsize into whatever space is left.
      [theme.breakpoints.down("sm")]: {
        right: t.bar.offset,
        justifyContent: "space-between",
      },
    },
    barMeta: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    },
    barLabel: {
      fontSize: t.bar.labelSize,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: signer.orange.main,
    },
    barName: {
      fontSize: t.bar.nameSize,
      fontWeight: 600,
      color: signer.ink,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      // A cap, not a floor — `barMeta`'s `minWidth: 0` lets it shrink and
      // ellipsize further when the bar is width-bounded on small screens.
      maxWidth: 200,
    },
    barButton: {
      flexShrink: 0,
      height: t.bar.buttonHeight,
      borderRadius: t.bar.buttonRadius,
      textTransform: "none",
      fontSize: t.bar.buttonFontSize,
      fontWeight: 600,
      px: 1.75,
      color: signer.ink,
      border: `1px solid ${signer.border.strong}`,
      "&:hover": {
        bgcolor: signer.surface.subtle,
        borderColor: signer.ink,
      },
      "&.Mui-disabled": { color: signer.disabled },
    },

    // Below `md`: a small icon-only circle, top-right — clear of the sticky
    // header's own content (which sits left/center) and nowhere near the
    // bottom Finish bar / AI FAB. Tap opens the same confirm-and-return as
    // the desktop bar; there's no room here for the signer-name label.
    barCompact: {
      position: "fixed",
      top: t.bar.offsetMobile,
      right: t.bar.offsetMobile,
      zIndex: t.barZ,
      display: { xs: "flex", md: "none" },
      width: t.bar.compactSize,
      height: t.bar.compactSize,
      minWidth: 0,
      p: 0,
      borderRadius: "50%",
      bgcolor: white,
      color: signer.orange.dark,
      border: `1px solid ${signer.border.main}`,
      boxShadow: `0 8px 20px ${alpha(signer.ink, 0.22)}`,
      "&:hover": { bgcolor: signer.surface.subtle },
      "&.Mui-disabled": { color: signer.disabled },
      "& svg": { fontSize: t.bar.compactIconSize },
    },
  };

  return styles;
};

export default inPersonStyles;
